import { Aide } from '@/components/ui/aide';
import React, { useMemo } from 'react';
import { Check, Truck } from 'lucide-react';

import { cn, formatVolume } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { libelle, nomLisible, texteSur } from '@/lib/libelles';

/**
 * Choisir les camions d'une commande, au moment où on la saisit.
 *
 * Le produit demandait jusqu'ici de créer la commande, de créer un convoi, de
 * faire avancer chaque camion jusqu'au chargement, de saisir son volume mesuré,
 * et seulement alors d'ouvrir un écran de répartition pour relier les deux. Le
 * geste qui intéresse l'exploitant — « cette commande part sur ces camions » —
 * arrivait en seizième position.
 *
 * Deux règles de lecture, et elles comptent toutes les deux:
 *
 * « citerne » contre « chargé ». Un camion qui n'a pas encore chargé annonce la
 * contenance de sa citerne, pas une mesure. Les deux valent le même nombre à
 * l'écran; seul l'exploitant peut juger de la différence, donc l'écran la dit.
 *
 * Un camion déjà engagé ailleurs est **proposé**, pas caché — avec le volume
 * qui lui reste. Une citerne de 45 000 L partie pour une commande de 12 000 L
 * en garde 33 000 à vendre, et les cacher n'était pas une décision
 * commerciale: c'était une conséquence du schéma, qui ne savait porter qu'une
 * commande par camion.
 */

const etiquetteStatut = {
  CREATED: 'planifié',
  EN_ROUTE_TO_BORDER: 'vers la frontière',
  AT_CUSTOMS_EXIT: 'douane de sortie',
  CLEARED_CUSTOMS_EXIT: 'douane de sortie passée',
  EN_ROUTE_TO_LOADING: 'vers le chargement',
  LOADING: 'en chargement',
  EN_ROUTE_TO_ENTRY_CUSTOMS: "vers la douane d'entrée",
  AT_CUSTOMS_ENTRY: "à la douane d'entrée",
  CLEARED_CUSTOMS_ENTRY: 'entré au Mali',
  ARRIVED_AT_DEPOT: 'au dépôt',
  EN_ROUTE_TO_DELIVERY: 'en livraison',
};

export const CamionsAAllouer = ({
  camions = [],
  selection = {},
  onToggle,
  cible = 0,
  chargement = false,
  className,
}) => {
  const couvert = useMemo(
    () =>
      Object.values(selection).reduce(
        (total, volume) => total + Number(volume || 0),
        0,
      ),
    [selection],
  );

  const part = cible > 0 ? Math.min(100, (couvert / cible) * 100) : 0;
  const suffisant = cible > 0 && couvert >= cible;

  if (chargement) {
    return (
      <p className={cn('text-sm text-muted-foreground', className)}>
        Recherche des camions disponibles…
      </p>
    );
  }

  if (camions.length === 0) {
    return (
      <div className={cn('rounded-xl border border-dashed border-border/70 p-4', className)}>
        <div className="flex items-center gap-1">
          <p className="text-sm font-medium text-foreground">
          Aucun camion en route pour l’instant
        </p>
          <Aide titre="Aucun camion en route pour l’instant">Créez un convoi pour faire partir des camions : ils apparaîtront ici dès leur départ, même avant d’avoir chargé. La commande peut être enregistrée sans camion et recevoir les siens plus tard.</Aide>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('space-y-2', className)}>
      <ul className="space-y-1.5">
        {camions.map((camion) => {
          const choisi = selection[camion.trip_id] != null;
          const reste = Number(camion.available_quantity || 0);
          // Non chargé: le reste affiché est la citerne. `is_loaded` suit la
          // règle du stock; une réponse plus ancienne ne le porte pas.
          const citerne =
            typeof camion.is_loaded === 'boolean'
              ? !camion.is_loaded
              : camion.volume_is_measured === false;
          const engage =
            Number(camion.reference_quantity || 0) > reste &&
            Number(camion.reference_quantity || 0) > 0;

          return (
            <li key={camion.trip_id}>
              <button
                type="button"
                onClick={() => onToggle?.(camion)}
                className={cn(
                  'flex w-full items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors',
                  choisi
                    ? 'border-primary/50 bg-primary/[0.06]'
                    : 'border-border/60 hover:border-primary/30 hover:bg-muted/40',
                )}
              >
                <span
                  className={cn(
                    'flex h-5 w-5 shrink-0 items-center justify-center rounded-md border',
                    choisi
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-border',
                  )}
                  aria-hidden="true"
                >
                  {choisi ? <Check className="h-3.5 w-3.5" /> : null}
                </span>

                <Truck
                  className="h-4 w-4 shrink-0 text-muted-foreground"
                  aria-hidden="true"
                />

                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold text-foreground">
                    {nomLisible(camion.truck_matricule, 'Camion')}
                  </span>
                  <span className="block truncate text-xs text-muted-foreground">
                    {etiquetteStatut[camion.trip_status] ||
                      libelle('tripStatus', camion.trip_status).toLowerCase()}
                    {camion.driver_name ? ` · ${texteSur(camion.driver_name, '')}` : ''}
                  </span>
                </span>

                <span className="shrink-0 text-right">
                  <span className="block text-sm font-semibold tabular-nums text-foreground">
                    {formatVolume(reste)}
                  </span>
                  <span className="block text-[11px] text-muted-foreground">
                    {citerne ? 'citerne' : 'chargé'}
                    {engage ? ' · déjà engagé' : ''}
                  </span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>

      {cible > 0 ? (
        <div className="flex items-center gap-3 rounded-xl border border-border/60 px-3 py-2">
          <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
            <div
              className={cn(
                'h-full rounded-full transition-all',
                suffisant ? 'bg-success' : 'bg-flame',
              )}
              style={{ width: `${part}%` }}
            />
          </div>
          <Badge
            className={cn(
              'shrink-0 text-[11px]',
              suffisant
                ? 'bg-success/15 text-success'
                : 'bg-flame/15 text-flame-ink',
            )}
          >
            {formatVolume(couvert)} / {formatVolume(cible)}
          </Badge>
        </div>
      ) : null}
    </div>
  );
};

export default CamionsAAllouer;
